import { APIError } from '@localagent/sdk';
import type {
  ListAccountMCPsResponse,
  ListMCPsResponse,
  MCPStatusInfo,
  McpControlAction,
  McpObservedState,
} from '@localagent/sdk';

export interface ToolsServerRow extends MCPStatusInfo {
  device_id: string;
  device_name: string | null;
  on_this_computer: boolean;
}

export interface ToolsServers {
  rows: ToolsServerRow[];
  device_reachable: boolean | undefined;
}

export function mergeToolsServers(
  device: ListMCPsResponse,
  account: ListAccountMCPsResponse,
  thisDevice: string,
): ToolsServers {
  const onComputers = new Set(account.mcps.map((m) => m.mcp_id));
  const platformRows: ToolsServerRow[] = device.mcps
    .filter((m) => !onComputers.has(m.mcp_id))
    .map((m) => ({ ...m, device_id: thisDevice, device_name: null, on_this_computer: false }));
  const computerRows: ToolsServerRow[] = account.mcps
    .map((m) => ({ ...m, on_this_computer: m.device_id === thisDevice }))
    .sort(
      (a, b) =>
        Number(b.on_this_computer) - Number(a.on_this_computer) ||
        a.device_name.localeCompare(b.device_name) ||
        a.name.localeCompare(b.name),
    );
  return { rows: [...computerRows, ...platformRows], device_reachable: device.device_reachable };
}

export function lifecycleActions(state: McpObservedState): McpControlAction[] {
  switch (state) {
    case 'running':
      return ['stop', 'restart'];
    case 'starting':
      return ['stop'];
    case 'stopped':
    case 'crashed':
      return ['start'];
    case 'unknown':
    case 'daemon_update_required':
    case 'not_applicable':
      return [];
  }
}

export function observedStateLabel(row: Pick<ToolsServerRow, 'observed_state' | 'device_name'>): string | null {
  switch (row.observed_state) {
    case 'running':
      return 'running';
    case 'starting':
      return 'starting';
    case 'stopped':
      return 'stopped';
    case 'crashed':
      return 'crashed';
    case 'unknown':
      return 'computer offline';
    case 'daemon_update_required':
      return `update ShogunChop on ${row.device_name}`;
    case 'not_applicable':
      return null;
  }
}

const MCP_COMMAND_CODES = new Set([
  'device_offline',
  'daemon_update_required',
  'daemon_error',
  'outcome_unknown',
  'not_found',
  'not_a_local_server',
  'overridden',
  'local_server_definition_refused',
  'not_running_on_device',
  'no_local_computer',
  'duplicate_name',
  'spawn_failed',
  'handshake_failed',
  'report_failed',
  'persist_failed',
  'edit_failed',
  'rename_not_supported',
  'invalid_edit',
  'invalid_env',
  'user_env_unavailable',
  'removed_during_add',
  'add_failed',
]);

export function mcpCommandErrorMessage(error: Error, onThisComputer: boolean): string {
  if (!(error instanceof APIError) || !MCP_COMMAND_CODES.has(error.code)) return error.message;
  if (error.code === 'device_offline' && onThisComputer) {
    return "ShogunChop on this computer isn't connected — choose Quit ShogunChop in the menu bar to stop all its servers";
  }
  const detail = (error.detail as { detail?: unknown }).detail;
  if (typeof detail !== 'string') {
    throw new Error(`MCP command error ${error.code} arrived without its message`);
  }
  return detail;
}

export function overriddenMessage(state: string): string {
  return `Overridden by a later command — the server is now ${state}`;
}
